import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { getLiveVideosRequest } from "../../../modules/admin-modules/liveVideos/Actions";
import { livevideoReportRequest } from "../../../modules/admin-modules/liveVideos/Actions";
import { centerHwRequest } from "../../../modules/admin-modules/reports/Actions";

const CAMERA_OPTIONS = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];

const CLASS_WORK_OPTIONS = [
  { value: "Excellent", label: "Excellent" },
  { value: "V.Good", label: "V.Good" },
  { value: "Good", label: "Good" },
  { value: "Fair", label: "Fair" },
  { value: "Not submitted", label: "Not submitted" },
  { value: "Incomplete", label: "Incomplete" },
];

const DEFAULT_ROW = {
  camera: "on",
  grade: "Excellent",
};

function getRowFields(rowState, studentId) {
  return { ...DEFAULT_ROW, ...(rowState[studentId] || {}) };
}

function cameraLabel(camera) {
  if (camera === "on") return "On";
  if (camera === "off") return "Off";
  return "—";
}

function formatJoinedAt(isoString) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return String(isoString);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(isoString);
  }
}

function buildParentMessage({
  subjectName,
  sessionTitle,
  studentName,
  camera,
  grade,
  joinedAt,
  customMessage,
}) {
  const subj = subjectName?.trim() || "your subject";
  const session = sessionTitle?.trim() || "Live session";
  const student = studentName?.trim() || "Student";
  const extra = customMessage?.trim();
  const joinedLine =
    joinedAt != null && String(joinedAt).trim()
      ? `Student joined the live at ${formatJoinedAt(joinedAt)}`
      : "";

  const lines = [
    `Edutopia — ${subj}`,
    `Live session attendance update`,
    `Session: ${session}`,
    `Student: ${student}`,
    `Camera: ${cameraLabel(camera)}`,
    `Class work evaluation: ${grade || "—"}`,
  ];
  if (extra) lines.push(extra);
  lines.push(`Thank you.`);
  if (joinedLine) lines.push(joinedLine);

  return lines.join("\n");
}

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";

const liveSelectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...liveSelectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  menuList: (styles) => ({
    ...liveSelectBase.menuList(styles),
    padding: "4px",
    overflowX: "hidden",
    overflowY: "auto",
  }),
  control: (styles, state) => ({
    ...liveSelectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

/** Compact row selects — theme-aware for dark mode */
const inlineSelectStyles = {
  ...liveSelectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...liveSelectBase.control(styles, state),
    minHeight: "38px",
    minWidth: "120px",
    fontSize: "14px",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: state.isDisabled ? 0.55 : 1,
  }),
  valueContainer: (styles) => ({ ...styles, padding: "0 8px" }),
  menuList: (styles) => ({
    ...liveSelectBase.menuList(styles),
    padding: "4px",
    overflowX: "hidden",
    overflowY: "auto",
  }),
  option: (styles, opts) => ({
    ...liveSelectBase.option(styles, opts),
    borderRadius: "8px",
    margin: "2px 0",
    padding: "8px 10px",
    fontSize: "14px",
  }),
  placeholder: (styles) => ({
    ...liveSelectBase.placeholder(styles),
    fontSize: "13px",
  }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    padding: "4px",
    color: state.isFocused ? brandPalette.primary : "var(--text-muted)",
  }),
};

const LiveVideoAttendanceReport = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user.userData);
  const subjectName = user?.subject?.name || "";

  const liveVideos = useSelector((s) => s.adminLiveVideos.liveVideos);
  const liveVideosReport = useSelector((s) => s.adminLiveVideos.liveVideosReport);
  const liveListLoading = useSelector((s) => s.adminLiveVideos.isLoading);
  const reportSending = useSelector((s) => s.reports.isLoading);

  const [liveVideoId, setLiveVideoId] = useState("");
  const [rowState, setRowState] = useState({});

  useEffect(() => {
    dispatch(getLiveVideosRequest());
  }, [dispatch]);

  useEffect(() => {
    setRowState({});
    if (!liveVideoId) return;
    dispatch(livevideoReportRequest(liveVideoId));
  }, [liveVideoId, dispatch]);

  const liveOptions = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    return raw.map((v) => ({
      value: String(v.id),
      label: v.title || `Live #${v.id}`,
    }));
  }, [liveVideos]);

  const selectedLive = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    return raw.find((v) => String(v.id) === String(liveVideoId)) || null;
  }, [liveVideos, liveVideoId]);

  const sessionTitle = selectedLive?.title || "";

  const students = useMemo(() => {
    if (liveVideosReport == null) return [];

    if (Array.isArray(liveVideosReport)) {
      const video = liveVideosReport.find((v) => String(v?.id) === String(liveVideoId));
      const list = video?.students ?? [];
      return Array.isArray(list) ? list : [];
    }

    const list = liveVideosReport.students ?? liveVideosReport;
    return Array.isArray(list) ? list : [];
  }, [liveVideosReport, liveVideoId]);

  const updateRowField = useCallback((studentId, field, value) => {
    setRowState((prev) => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), [field]: value },
    }));
  }, []);

  const sendRowToParent = useCallback(
    (studentId, studentName, joinedAtIso) => {
      const st = getRowFields(rowState, studentId);
      if (!st.camera || !st.grade) return;
      const evaluation = buildParentMessage({
        subjectName,
        sessionTitle,
        studentName,
        camera: st.camera,
        grade: st.grade,
        joinedAt: joinedAtIso,
        customMessage: st.customMessage,
      });
      dispatch(
        centerHwRequest({
          student_id: studentId,
          evaluation,
        })
      );
    },
    [dispatch, subjectName, sessionTitle, rowState]
  );

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={5}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Live video attendance report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Select a live session, choose camera status and class-work evaluation for each student,
              then click Send to notify the parent (same channel as center homework notifications).
              Optional notes in each row are included only for that student&apos;s message.
            </Text>
          </Box>
          <Flex align="flex-start" gap={3} flexWrap="wrap" justifyContent={{ base: "flex-start", md: "flex-end" }}>
            <Box minW={{ base: "100%", md: "320px" }} maxW={{ base: "100%", md: "420px" }}>
              <ReactSelect
                options={liveOptions}
                isLoading={liveListLoading}
                placeholder="Select live video"
                styles={selectStyles}
                value={liveOptions.find((o) => o.value === String(liveVideoId)) || null}
                onChange={(opt) => setLiveVideoId(opt?.value ? String(opt.value) : "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </Box>
            <LogoutMenu />
          </Flex>
        </Flex>

        <Box
          border="1px solid"
          borderColor="var(--border-subtle)"
          bg="var(--surface-raised)"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.12)"
          overflowX="auto"
          color="var(--text-primary)"
        >
          {!liveVideoId ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Choose a live video to load attendees.
            </Text>
          ) : liveListLoading && students.length === 0 ? (
            <Text fontWeight="600" color="var(--text-primary)">
              Loading attendees…
            </Text>
          ) : students.length === 0 ? (
            <Text fontWeight="600" color="var(--text-muted)">
              No students returned for this session.
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th color="var(--text-primary)">#</Th>
                  <Th color="var(--text-primary)">Student</Th>
                  <Th color="var(--text-primary)">Code</Th>
                  <Th color="var(--text-primary)">Phone</Th>
                  <Th color="var(--text-primary)">Parent phone</Th>
                  <Th color="var(--text-primary)">Joined live at</Th>
                  <Th color="var(--text-primary)">Camera</Th>
                  <Th color="var(--text-primary)">Class work</Th>
                  <Th color="var(--text-primary)" minW="200px">
                    Optional note
                  </Th>
                  <Th color="var(--text-primary)">Send</Th>
                </Tr>
              </Thead>
              <Tbody>
                {students.map((row, index) => {
                  const id = row.student?.id ?? row.id;
                  if (id == null) return null;
                  const st = getRowFields(rowState, id);
                  const name = row.student?.name ?? row.name ?? "—";
                  const code = row.user_name ?? row.code ?? "—";
                  const joinedAt = row.pivot?.created_at;
                  const joinedDisplay = joinedAt ? formatJoinedAt(joinedAt) : "—";
                  const canSend = Boolean(st.camera && st.grade);
                  return (
                    <Tr key={id}>
                      <Td fontWeight="700">#{index + 1}</Td>
                      <Td fontWeight="600">{name}</Td>
                      <Td>{code}</Td>
                      <Td>{row.phone || "—"}</Td>
                      <Td>{row.parent_phone || "—"}</Td>
                      <Td fontSize="sm" whiteSpace="nowrap">
                        {joinedDisplay}
                      </Td>
                      <Td minW="148px" verticalAlign="middle">
                        <ReactSelect
                          instanceId={`cam-${id}`}
                          inputId={`cam-${id}`}
                          options={CAMERA_OPTIONS}
                          styles={inlineSelectStyles}
                          placeholder="Camera"
                          isClearable
                          isSearchable={false}
                          isDisabled={reportSending}
                          value={CAMERA_OPTIONS.find((o) => o.value === st.camera) ?? null}
                          onChange={(opt) => updateRowField(id, "camera", opt?.value ?? "")}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </Td>
                      <Td minW="168px" verticalAlign="middle">
                        <ReactSelect
                          instanceId={`grade-${id}`}
                          inputId={`grade-${id}`}
                          options={CLASS_WORK_OPTIONS}
                          styles={inlineSelectStyles}
                          placeholder="Evaluation"
                          isClearable
                          isSearchable={false}
                          isDisabled={reportSending}
                          value={CLASS_WORK_OPTIONS.find((o) => o.value === st.grade) ?? null}
                          onChange={(opt) => updateRowField(id, "grade", opt?.value ?? "")}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </Td>
                      <Td minW="200px" maxW="280px" verticalAlign="middle">
                        <Textarea
                          value={st.customMessage ?? ""}
                          onChange={(e) => updateRowField(id, "customMessage", e.target.value)}
                          placeholder="Optional…"
                          size="sm"
                          rows={2}
                          resize="vertical"
                          borderRadius="12px"
                          borderColor="var(--border-subtle)"
                          _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
                          fontWeight="600"
                          color="var(--text-primary)"
                          bg="var(--surface-muted)"
                          isDisabled={reportSending}
                        />
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          borderRadius="lg"
                          bg={brandPalette.secondary}
                          color="white"
                          fontWeight="800"
                          isDisabled={!canSend || reportSending}
                          _hover={{ bg: brandPalette.primary }}
                          onClick={() => sendRowToParent(id, name, joinedAt)}
                        >
                          Send
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default LiveVideoAttendanceReport;
